import {useState} from 'react';
import {Button, Card, Form, Spinner} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import {useRunPing} from '../../Hooks/RouterManager';

interface PingPageProps {
  routerId: string;
}

const PingPage = ({routerId}: PingPageProps): JSX.Element => {
  const intl = useIntl();
  const [destination, setDestination] = useState('');
  const [validated, setValidated] = useState(false);
  const {mutate, data: result, isLoading: loading, reset} = useRunPing(routerId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!destination.trim()) return;
    reset();
    mutate(destination.trim());
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>
          <Level3Title title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.TITLE'})} />
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-4">
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.SUBTITLE'})}
        </p>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <div className="row g-3 align-items-end">
            <div className="col-md-8">
              <Form.Group>
                <Form.Label className="required">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.TARGET'})}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="google.com ou 8.8.8.8"
                  required
                  disabled={loading}
                  isInvalid={validated && !destination.trim()}
                />
                <Form.Control.Feedback type="invalid">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.REQUIRED'})}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.RUNNING'})}
                  </>
                ) : (
                  intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.RUN'})
                )}
              </Button>
            </div>
          </div>
        </Form>

        {result && (
          <div className="mt-6 p-4 bg-light rounded">
            <div className="fw-bold mb-3">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.RESULTS'})} {result.destination}
            </div>
            <div className="row">
              <div className="col-md-6">
                <p>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.SENT'})} :{' '}
                  <strong>{result.sent}</strong>
                </p>
                <p>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.RECEIVED'})} :{' '}
                  <strong>{result.received}</strong>
                </p>
                <p>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.LOSS'})} :{' '}
                  <strong>{result.lossPercent}%</strong>
                </p>
              </div>
              <div className="col-md-6">
                <p>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.MIN'})} :{' '}
                  <strong>{result.minMs} ms</strong>
                </p>
                <p>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.MAX'})} :{' '}
                  <strong>{result.maxMs} ms</strong>
                </p>
                <p>
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.PING.AVG'})} :{' '}
                  <strong>{result.avgMs} ms</strong>
                </p>
              </div>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PingPage;
